import Head from 'next/head'
import { useEffect } from 'react'
import isEmpty from 'lodash/isEmpty'
import { GetServerSideProps } from 'next/types'

import store from '@/store'
import { agentApi } from '@/services/agent-api'
import { StoreProvider } from '@/providers/store'
import AgentsTable from '@/components/AgentsTable'
import { baseDimensions } from '@/store/models/search'
import TableSubHeader from '@/components/TableSubHeader'
import { updateHeadings, updateTitleTag } from '@/utils/seo'
import NearbyLocations from '@/components/NearbyLocations'
import { LocationsNearbyResponse } from '@/types/locations'
import { AgentSearchCriteria, AgentSearchResponse } from '@/types/agents'
import Disclosure from '@/components/Disclosure'
import BelowTableContent from '@/components/BelowTableContent'
import bamEvents from '@/services/bam-events'

interface Props {
  city: string
  state: string
  zipcode: string
  embedded: boolean
  startupDimensions: AgentSearchCriteria
  startupQueryResponse: AgentSearchResponse
  startupLocationsNearbyResponse: LocationsNearbyResponse
}

const LocalizedPage = (props: Props) => {
  store.getActions().search.setInitialSSRData({
    dimensions: props.startupDimensions,
    queryResponse: props.startupQueryResponse,
    locationsNearbyResponse: props.startupLocationsNearbyResponse,
    embedded: props.embedded
  })
  store.getActions().search.setH1Tag(updateHeadings(props.city, props.state))

  useEffect(() => {
    // if user lands on index page, prefill zipcode
    if (!/agents\/[\w]+/.test(window.location.pathname)) {
      const zipcode =
        props.startupQueryResponse.location_data.primary_location.zipcode
      if (zipcode) {
        store.getActions().search.setZipcode(zipcode)
      }
    }
  }, [props.startupQueryResponse.location_data.primary_location.zipcode])

  useEffect(() => {
    // fire for initial load
    try {
      bamEvents.onResultsChanged()
    } catch (error) {
      console.log(error)
    }
  }, [])

  return (
    <>
      <StoreProvider>
        <div
          id="agent-results-container"
          className={`${props.embedded ? 'embedded' : ''}`}
        >
          <Head>
            <title>{updateTitleTag(props.city, props.state)}</title>
            <meta name="re:primary_location:state" content={props.state} />
            <meta name="re:primary_location:city" content={props.city} />
            <meta name="re:primary_location:zipcode" content={props.zipcode} />
          </Head>
          <script
            dangerouslySetInnerHTML={{
              __html: `
                  /* fix Myfi widget tracking */
                  if(typeof cohesion === "function" && window.INLINE_DATA) {
                    cohesion("tagular:ready", function () {
                      if (window._Tagular && _Tagular.webContext && _Tagular.webContext.page) {
                        _Tagular.webContext.page.referrer = window.INLINE_DATA.foreignReferrerUrl;
                      }
                    });
                  }
                `
            }}
          ></script>
          {!props.embedded && (
            <>
              <div className="flex md:items-center flex-col-reverse md:flex-row">
                <div className="flex-1">
                  <h1 className="my-6" id="title-line">
                    {updateHeadings(props.city, props.state)}
                  </h1>
                </div>
                <div className="text-right md:text-left">
                  <Disclosure />
                </div>
              </div>
              <TableSubHeader />
            </>
          )}
          <AgentsTable />
          {!props.embedded && (
            <>
              <NearbyLocations showHeading={true} hideOnNoResults={true} />
              <BelowTableContent />
            </>
          )}
        </div>
      </StoreProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const [state, city] = (query.location as string[]) || []
  const limit = parseInt(query.limit as string) || 15

  const isValidLocation = !isEmpty(state) && !isEmpty(city)

  const zipcode = query.zipcode
    ? query.zipcode.toString()
    : !isValidLocation
    ? '33130'
    : null

  const queryDimensions = {
    ...baseDimensions,
    zipcode: zipcode,
    state: isValidLocation ? state : null,
    city: isValidLocation ? city.replace(/-/g, ' ') : null,
    limit
  }

  const queryResponse = await agentApi.fetchAgents(queryDimensions)

  if (
    queryResponse.location_data.primary_location.state === '' ||
    queryResponse.location_data.primary_location.city === '' ||
    queryResponse.location_data.primary_location.zipcode === ''
  ) {
    throw new Error('Location not found')
  }

  let locationsNearbyResponse: LocationsNearbyResponse = null

  if (queryResponse.location_data.primary_location.zipcode) {
    const searchPrimaryLocationZipcode =
      queryResponse.location_data.primary_location.zipcode

    const locationNearbyResponse = await agentApi.fetchNearbyLocations(
      searchPrimaryLocationZipcode
    )

    if (!queryDimensions.zipcode) {
      queryDimensions.zipcode = searchPrimaryLocationZipcode
    }

    locationsNearbyResponse = locationNearbyResponse
  }

  const embedded = (query.embedded as string) === 'true'

  return {
    props: {
      startupDimensions: { ...queryDimensions, city: null, state: null },
      embedded,
      startupQueryResponse: queryResponse,
      startupLocationsNearbyResponse: locationsNearbyResponse,
      city: queryResponse.location_data.primary_location.city || city || '',
      state: queryResponse.location_data.primary_location.state || state || '',
      zipcode:
        queryResponse.location_data.primary_location.zipcode || zipcode || ''
    }
  }
}

export default LocalizedPage
